<template>
  <div class="code-block">
    <div class="code-head">
      <div class="code-tabs">
        <span class="code-dot red"></span>
        <span class="code-dot yellow"></span>
        <span class="code-dot green"></span>
        <span class="code-title">{{ title || langLabel }}</span>
      </div>
      <button class="copy-btn" :class="{ copied }" @click="copyCode">
        <i :class="copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
        {{ copied ? '已复制' : '复制' }}
      </button>
    </div>
    <pre class="code-body"><code :class="`language-${lang}`" ref="codeRef">{{ code }}</code></pre>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue'
import hljs from 'highlight.js/lib/core'

const props = defineProps({
  code: { type: String, required: true },
  lang: { type: String, default: 'kotlin' },
  title: { type: String, default: '' }
})

const codeRef = ref(null)
const copied = ref(false)

const langLabels = { kotlin: 'Kotlin', java: 'Java', xml: 'XML', gradle: 'Gradle', bash: 'Bash', html: 'HTML' }
const langLabel = langLabels[props.lang] || props.lang

const highlight = () => {
  if (codeRef.value) {
    delete codeRef.value.dataset.highlighted
    codeRef.value.removeAttribute('data-highlighted')
    codeRef.value.className = `language-${props.lang}`
    if (hljs.getLanguage(props.lang)) {
      const result = hljs.highlight(props.code, { language: props.lang })
      codeRef.value.innerHTML = result.value
    } else {
      codeRef.value.textContent = props.code
    }
  }
}

onMounted(async () => {
  await nextTick()
  highlight()
})

watch(() => props.code, () => {
  nextTick(highlight)
})

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(props.code)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1800)
  } catch (e) {
    const ta = document.createElement('textarea')
    ta.value = props.code
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1800)
  }
}
</script>

<style scoped>
.code-block {
  background: #0d1117;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}
.code-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #161b22;
  border-bottom: 1px solid var(--border);
}
.code-tabs { display: flex; align-items: center; gap: 8px; }
.code-dot { width: 11px; height: 11px; border-radius: 50%; }
.code-dot.red { background: #ff5f56; }
.code-dot.yellow { background: #ffbd2e; }
.code-dot.green { background: #27c93f; }
.code-title {
  margin-left: 10px;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-muted);
  letter-spacing: 0.02em;
}
.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 0.76rem;
  color: var(--text-muted);
  border: 1px solid var(--border);
  border-radius: 6px;
  transition: all 0.2s ease;
}
.copy-btn:hover {
  color: var(--accent);
  border-color: var(--accent);
}
.copy-btn.copied {
  color: var(--android);
  border-color: var(--android);
}
.code-body {
  margin: 0;
  padding: 18px 20px;
  overflow-x: auto;
  font-family: var(--font-mono);
  font-size: 0.84rem;
  line-height: 1.75;
  color: #c9d1d9;
}
.code-body code {
  font-family: inherit !important;
  background: transparent !important;
  padding: 0 !important;
  white-space: pre;
}
</style>
